import React, { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { waitFor, onMount } from 'lp-hoc'
import * as globalActions from 'artist-portal-actions'
import { SectionBlock } from 'components'
import { SchoolYearTabBar, ProgramTable } from '../components'
import {
  PartnerPartnershipTableForm,
  SchoolPartnershipTableForm,
} from '../forms'
import * as apiActions from 'api-actions'
import * as actions from '../actions'
import { selectors as globalArtistSelectors } from 'artist-portal-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import * as Types from 'types'
import { displaySubmitFailure, useUnmount, useCommunity } from 'utils'
import * as flashActions from 'redux-flash'
import { reset, destroy } from 'redux-form'
import { mapValues, first, groupBy, get } from 'lodash'
import syncProfileCompletion from '../sync-profile-completion'

const propTypes = {
  currentSchoolYear: Types.schoolYear.isRequired,
  destroyForms: PropTypes.func.isRequired,
  partnerOptions: PropTypes.arrayOf(Types.searchSuggestion),
  setArtist: PropTypes.func.isRequired,
  updateArtist: PropTypes.func.isRequired,
  activeSchoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  badgeQuestions: PropTypes.arrayOf(Types.badgeQuestion).isRequired,
  schoolOptions: PropTypes.arrayOf(Types.searchSuggestion).isRequired,
  artist: Types.artist,
}

const defaultProps = {
  updatedPartnerships: [],
}

function ProgramDelivery({
  artist,
  currentSchoolYear,
  destroyForms,
  partnerOptions,
  activeSchoolYears,
  updateArtist,
  setArtist,
  flashSuccessMessage,
  resetForm,
  schoolOptions,
}) {
  const community = useCommunity()
  const { teachingArtist } = community
  const isProvideOwnPrograms = teachingArtist.provideOwnPrograms

  const [selectedSchoolYear, setSelectedSchoolYear] =
    useState(currentSchoolYear)

  const schoolYearsById = useMemo(() => {
    return mapValues(groupBy(activeSchoolYears, 'id'), first)
  }, [activeSchoolYears])
  // Allow partners to edit the current and previous school year
  const editableSchoolYearIds = useMemo(() => {
    return activeSchoolYears.slice(0, 2).map(({ id }) => id)
  }, [activeSchoolYears])

  const { id: artistId } = artist

  const partnerships = useMemo(() => {
    return artist.partnerships
  }, [artist.partnerships])

  const taPrograms = useMemo(() => {
    return artist.taPrograms
  }, [artist.taPrograms])

  const updateArtistForSchoolYear = useCallback(
    (params) => {
      return updateArtist(artistId, selectedSchoolYear.id, params)
    },
    [artistId, selectedSchoolYear.id]
  )

  const handleSuccess = (updatedArtist) => {
    setArtist(updatedArtist)
    syncProfileCompletion({
      artist: updatedArtist,
      previousArtist: artist,
      community,
      updateArtistBadge: updateArtistForSchoolYear,
    })
    flashSuccessMessage('Artist information updated!')
  }

  // Do not destroy forms as the user navigates between school year tabs, only on unmount of the parent component
  useUnmount(() => {
    const tabFormNames = activeSchoolYears.map((year) => {
      return `artist-partnership-table-${year.id}`
    })
    destroyForms(...tabFormNames)
  })

  const previousPartnerships = useMemo(() => {
    // Not worth calculating previous partnerships for years that can't copy them over
    if (!editableSchoolYearIds.includes(selectedSchoolYear.id)) return []
    return partnerships.filter(({ schoolYearId }) => {
      const existingPosition = get(
        schoolYearsById,
        `${schoolYearId}.position`,
        0
      )
      return existingPosition < selectedSchoolYear.position
    })
  }, [partnerships, schoolYearsById, selectedSchoolYear, editableSchoolYearIds])

  const previousPrograms = useMemo(() => {
    // Not worth calculating previous programs for years that can't copy them over
    if (!editableSchoolYearIds.includes(selectedSchoolYear.id)) return []
    return artist.taPrograms.filter(({ schoolYearId }) => {
      const existingPosition = get(
        schoolYearsById,
        `${schoolYearId}.position`,
        0
      )
      return existingPosition < selectedSchoolYear.position
    })
  }, [taPrograms, schoolYearsById, selectedSchoolYear, editableSchoolYearIds])

  const isEditable = editableSchoolYearIds.includes(selectedSchoolYear.id)

  const [showAddSchoolPartnershipModal, setShowAddSchoolPartnershipModal] =
    useState(false)
  const [showAddProgramModal, setShowAddProgramModal] = useState(false)
  const [showAddPartnershipModal, setShowAddPartnershipModal] = useState(false)

  const filteredPartnerPartnerships = partnerships.filter(
    (p) => p.schoolYearId === selectedSchoolYear.id && p.organizationId
  )
  const filteredSchoolPartnerships = partnerships.filter(
    (p) => p.schoolYearId === selectedSchoolYear.id && p.schoolId
  )
  const filteredTaPrograms = taPrograms.filter(
    (p) => p.schoolYearId === selectedSchoolYear.id
  )

  const hasSection = partnerships.length > 0 || taPrograms.length > 0

  return (
    <React.Fragment>
      <section className="card">
        <div className="flex-vertical flex-gap-1">
          <div>
            <h2>How do you deliver programs to schools?</h2>
            <p>
              Teaching artists deliver arts programs to schools in many ways. Do
              you partner with organizations, work independently with schools,
              or both? Select the option that best describes you to get started.
              If both apply, you can return later to select the other option.
            </p>
          </div>
          <div className="flex-horizontal flex-gap-1">
            <button
              className="button-secondary"
              onClick={() => setShowAddPartnershipModal(true)}
            >
              Do you work with associated organization?
            </button>
            {isProvideOwnPrograms && (
              <button
                className="button-primary"
                onClick={() => setShowAddProgramModal(true)}
              >
                Do you provide your own programs?
              </button>
            )}
          </div>
        </div>
      </section>
      <div className="partnerships-year-select">
        {hasSection && (
          <SchoolYearTabBar
            schoolYears={activeSchoolYears}
            schoolYear={selectedSchoolYear}
            onChange={setSelectedSchoolYear}
          />
        )}
        <SectionBlock>
          <div className="flex-vertical flex-gap-5">
            <PartnerPartnershipTableForm
              key={`partner-partnership-${selectedSchoolYear.id}`} // important for triggering renders when switching tabs
              name={`partner-partnership-table-${selectedSchoolYear.id}`}
              initialValues={{
                partnerships: filteredPartnerPartnerships,
              }}
              onSubmit={updateArtistForSchoolYear}
              onSubmitSuccess={(partner) => {
                resetForm(`partner-partnership-table-${selectedSchoolYear.id}`) // removes partnerships with _destroy flag
                handleSuccess(partner)
              }}
              onSubmitFail={displaySubmitFailure}
              disabled={!isEditable}
              previousPartnerships={previousPartnerships}
              partnerOptions={partnerOptions}
              schoolYears={activeSchoolYears}
              visibleSchoolYearId={selectedSchoolYear.id}
              updateArtist={updateArtist}
              artistId={artistId}
              setShowAddPartnershipModal={setShowAddPartnershipModal}
              showAddPartnershipModal={showAddPartnershipModal}
            />
            {isProvideOwnPrograms && (
              <ProgramTable
                key={`program-table-${selectedSchoolYear.id}`}
                name={`program-table-${selectedSchoolYear.id}`}
                initialValues={{
                  taPrograms: filteredTaPrograms,
                }}
                onSubmit={updateArtistForSchoolYear}
                onSubmitSuccess={(artist) => {
                  resetForm(`program-table-${selectedSchoolYear.id}`)
                  handleSuccess(artist)
                }}
                programsForSchoolYear={taPrograms.filter(
                  (p) => p.schoolYearId === selectedSchoolYear.id
                )}
                onSubmitFail={displaySubmitFailure}
                isEditable={isEditable}
                previousPrograms={previousPrograms}
                schoolYears={activeSchoolYears}
                visibleSchoolYearId={selectedSchoolYear.id}
                updateArtistForSchoolYear={updateArtistForSchoolYear}
                artistId={artistId}
                setArtist={setArtist}
                setShowAddSchoolPartnershipModal={
                  setShowAddSchoolPartnershipModal
                }
                setShowAddProgramModal={setShowAddProgramModal}
                showAddProgramModal={showAddProgramModal}
              />
            )}
            <SchoolPartnershipTableForm
              key={`school-partnership-${selectedSchoolYear.id}`}
              name={`school-partnership-table-${selectedSchoolYear.id}`}
              initialValues={{
                partnerships: filteredSchoolPartnerships,
              }}
              onSubmit={updateArtistForSchoolYear}
              onSubmitSuccess={(partner) => {
                resetForm(`school-partnership-table-${selectedSchoolYear.id}`)
                handleSuccess(partner)
              }}
              onSubmitFail={displaySubmitFailure}
              disabled={!isEditable}
              previousPartnerships={previousPartnerships}
              schoolOptions={schoolOptions}
              schoolYears={activeSchoolYears}
              visibleSchoolYearId={selectedSchoolYear.id}
              updateArtist={updateArtist}
              artistId={artistId}
              setShowAddSchoolPartnershipModal={
                setShowAddSchoolPartnershipModal
              }
              showAddSchoolPartnershipModal={showAddSchoolPartnershipModal}
            />
          </div>
        </SectionBlock>
      </div>
    </React.Fragment>
  )
}

ProgramDelivery.propTypes = propTypes
ProgramDelivery.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    artist: globalArtistSelectors.artist(state),
    currentArtistId: globalArtistSelectors.currentArtistId(state),
    partnerOptions: globalArtistSelectors.partnerOptions(state),
    schoolOptions: globalArtistSelectors.schoolOptions(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    activeSchoolYears: globalArtistSelectors.orderedActiveSchoolYears(state),
    badgeQuestions: globalSelectors.badgeQuestions(state),
  }
}

const mapDispatchToProps = {
  destroyForms: destroy,
  setPartnerships: actions.setPartnerships,
  fetchPartnerOptions: apiActions.fetchPartnerOptions,
  fetchSchoolOptions: apiActions.fetchSchoolOptions,
  flashSuccessMessage: flashActions.flashSuccessMessage,
  resetForm: reset,
  setArtist: globalActions.setArtist,
  updateArtist: apiActions.updateArtist,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  onMount('fetchPartnerOptions'),
  onMount('fetchSchoolOptions'),
  waitFor(['artist', 'partnerOptions', 'badgeQuestions', 'schoolOptions'])
)(ProgramDelivery)
